import { Option, Schema as S, pipe } from "effect";
import {
	Transition,
	defineRouteUnion,
	literal,
	mapTo,
	oneOf,
	parseUrlWithFallback,
	query,
	root,
	slash,
	string,
} from "foldkit/route";
import type { Url } from "foldkit/url";

/** Time-travel date on every route (KTD3). The URL key stays v1's `as_of`. */
const asOf = S.OptionFromOptional(S.String);
const AsOfQuery = S.Struct({ asOf }).pipe(S.encodeKeys({ asOf: "as_of" }));
/** KTD8: a card's Record link carries `via=keep-in-touch`; optional so existing constructors need no change. */
const via = S.optionalKey(S.OptionFromOptional(S.String));
const RecordQuery = S.Struct({ asOf, via }).pipe(
	S.encodeKeys({ asOf: "as_of" }),
);
/** v1 Searched keeps its search text in `q` (replace, not push); Tags links to `/?q=%23tag`. */
const SearchQuery = S.Struct({
	asOf,
	q: S.OptionFromOptional(S.String),
}).pipe(S.encodeKeys({ asOf: "as_of" }));
/** Stripe returns to `/billing?session_id=...`; the page strips it after reading it. */
const CreditsQuery = S.Struct({
	asOf,
	sessionId: S.OptionFromOptional(S.String),
}).pipe(S.encodeKeys({ asOf: "as_of", sessionId: "session_id" }));
/** First-run import is `/import?onboarding=1`; the flag only shows the Skip control. */
const ImportQuery = S.Struct({
	asOf,
	onboarding: S.OptionFromOptional(S.String),
}).pipe(S.encodeKeys({ asOf: "as_of" }));

export const Route = defineRouteUnion({
	Home: { asOf, q: S.OptionFromOptional(S.String) },
	Records: { asOf, q: S.OptionFromOptional(S.String) },
	Record: { id: S.String, asOf, via },
	Entry: { id: S.String, asOf },
	Settings: { asOf },
	Credits: { asOf, sessionId: S.OptionFromOptional(S.String) },
	Rates: { asOf },
	Developer: { asOf },
	Developers: { asOf },
	Admin: { asOf },
	Audit: { asOf },
	AI: { asOf },
	Chat: { asOf },
	Integrations: { asOf },
	Tags: { asOf },
	AttributeSchema: { asOf },
	KeepInTouch: { asOf },
	Import: { asOf, onboarding: S.OptionFromOptional(S.String) },
	/** The extension's capture page (KTD3); the payload never rides in the route. */
	Capture: { asOf },
	/** Rhysomn for Agents (behind `a2a-context`; gated in `routePageTag`). */
	Tasks: { asOf },
	Task: { id: S.String, asOf },
	/** `folder` filters the list to a folder and its subfolders; optional so existing constructors need no change. */
	Knowledge: { asOf, folder: S.optionalKey(S.OptionFromOptional(S.String)) },
	Note: { id: S.String, asOf },
	Agents: { asOf },
	NotFound: { path: S.String },
});
export type Route = typeof Route.Type;
export type RouteTag = Route["_tag"];

const page = (segment: string) => pipe(literal(segment), query(AsOfQuery));

export const routers = {
	Home: pipe(root, query(SearchQuery), mapTo(Route.Home)),
	Records: pipe(literal("records"), query(SearchQuery), mapTo(Route.Records)),
	Record: pipe(
		literal("records"),
		slash(string("id")),
		query(RecordQuery),
		mapTo(Route.Record),
	),
	Entry: pipe(
		literal("entries"),
		slash(string("id")),
		query(AsOfQuery),
		mapTo(Route.Entry),
	),
	Settings: pipe(page("settings"), mapTo(Route.Settings)),
	Credits: pipe(literal("billing"), query(CreditsQuery), mapTo(Route.Credits)),
	Rates: pipe(page("rates"), mapTo(Route.Rates)),
	Developer: pipe(page("developer"), mapTo(Route.Developer)),
	Developers: pipe(page("developers"), mapTo(Route.Developers)),
	Admin: pipe(page("admin"), mapTo(Route.Admin)),
	Audit: pipe(page("audit"), mapTo(Route.Audit)),
	AI: pipe(page("ai"), mapTo(Route.AI)),
	Chat: pipe(page("chat"), mapTo(Route.Chat)),
	Integrations: pipe(page("integrations"), mapTo(Route.Integrations)),
	Tags: pipe(page("tags"), mapTo(Route.Tags)),
	AttributeSchema: pipe(page("attribute-schema"), mapTo(Route.AttributeSchema)),
	KeepInTouch: pipe(page("keep-in-touch"), mapTo(Route.KeepInTouch)),
	Import: pipe(literal("import"), query(ImportQuery), mapTo(Route.Import)),
	Capture: pipe(page("capture"), mapTo(Route.Capture)),
	Tasks: pipe(page("tasks"), mapTo(Route.Tasks)),
	Task: pipe(
		literal("tasks"),
		slash(string("id")),
		query(AsOfQuery),
		mapTo(Route.Task),
	),
	Knowledge: pipe(
		literal("knowledge"),
		query(
			S.Struct({
				asOf,
				folder: S.optionalKey(S.OptionFromOptional(S.String)),
			}).pipe(S.encodeKeys({ asOf: "as_of" })),
		),
		mapTo(Route.Knowledge),
	),
	Note: pipe(
		literal("knowledge"),
		slash(string("id")),
		query(AsOfQuery),
		mapTo(Route.Note),
	),
	Agents: pipe(page("agents"), mapTo(Route.Agents)),
};

/**
 * Old bookmarks and links redirect to the new vocabulary: these parse the v1
 * `/nodes`, `/knots` and `/credits` URLs (query params, `as_of` included, are
 * parsed by the same schemas) into the same routes. They are parse-only — they
 * are not in `routers`, so `toHref` always prints the `/records`, `/entries`
 * and `/billing` form.
 */
const legacyRouters = [
	pipe(literal("credits"), query(CreditsQuery), mapTo(Route.Credits)),
	pipe(
		literal("nodes"),
		slash(string("id")),
		query(RecordQuery),
		mapTo(Route.Record),
	),
	pipe(literal("nodes"), query(SearchQuery), mapTo(Route.Records)),
	pipe(
		literal("knots"),
		slash(string("id")),
		query(AsOfQuery),
		mapTo(Route.Entry),
	),
] as const;

/** Actor-agnostic (KTD7): gating happens in the view. */
export const urlToRoute: (url: Url) => Route = parseUrlWithFallback(
	oneOf(
		routers.Record,
		routers.Records,
		routers.Entry,
		...legacyRouters,
		routers.Settings,
		routers.Credits,
		routers.Rates,
		routers.Developer,
		routers.Developers,
		routers.Admin,
		routers.Audit,
		routers.AI,
		routers.Chat,
		routers.Integrations,
		routers.Tags,
		routers.AttributeSchema,
		routers.KeepInTouch,
		routers.Import,
		routers.Capture,
		routers.Task,
		routers.Tasks,
		routers.Note,
		routers.Knowledge,
		routers.Agents,
		routers.Home,
	),
	Route.NotFound,
);

export const asOfOf = (route: Route): Option.Option<string> =>
	route._tag === "NotFound" ? Option.none() : route.asOf;

/** The one place `asOf` is carried across navigation (KTD3). */
export const withAsOf =
	(maybeAsOf: Option.Option<string>) =>
	<R extends Route>(route: R): R =>
		route._tag === "NotFound" ? route : { ...route, asOf: maybeAsOf };

export const keepAsOf = (current: Route) => withAsOf(asOfOf(current));

export const toHref = (route: Route): string =>
	route._tag === "NotFound"
		? route.path
		: (routers[route._tag].build as (value: object) => string)(route);

/** Builds every in-app link: the target route with the current route's `asOf`. */
export const linkHref = (current: Route, target: Route): string =>
	toHref(keepAsOf(current)(target));

export const transition = (
	previous: Route,
	next: Route,
): Transition.Transition<Route> => Transition.make(previous, next);
